import * as PopoverPrimitive from "@radix-ui/react-popover";
import type { ReactNode } from "react";
import { cn } from "../../lib/cn.js";

/**
 * The one floating surface every dropdown in this app sits on.
 *
 * Radix rather than a hand-rolled `absolute` div, for the three behaviours a
 * hand-rolled one always gets wrong: focus returns to the trigger on close,
 * Escape and an outside click both dismiss, and the panel flips when it would
 * render past the viewport. The screener's filter row sits near the right
 * edge, so the last one is not hypothetical.
 *
 * Deliberately thin. Only the surface, the border, and the width contract live
 * here; what goes inside is the caller's, so this file never grows a variant
 * per consumer.
 *
 * Square corners like everything else — docs/design.md puts radius at 0
 * outside the round dots. The shadow is the one exception the same section
 * grants: it marks a surface that floats above the document, which is exactly
 * what this is and nothing in the flow may claim.
 */

export const PopoverRoot = PopoverPrimitive.Root;
export const PopoverTrigger = PopoverPrimitive.Trigger;
export const PopoverAnchor = PopoverPrimitive.Anchor;

export interface PopoverPanelProps {
  children: ReactNode;
  className?: string;
  /** Distance from the trigger, in px. 4 keeps the 8pt rhythm at half-step. */
  sideOffset?: number;
  align?: "start" | "center" | "end";
  /**
   * Whether the panel takes focus when it opens.
   *
   * False for a panel whose trigger is a text input: the reader is typing, and
   * moving focus into the list swallows the next keystroke. The list is then
   * driven from the input's own arrow keys.
   */
  autoFocus?: boolean;
}

export function PopoverPanel({
  children,
  className,
  sideOffset = 4,
  align = "start",
  autoFocus = true,
}: PopoverPanelProps) {
  return (
    <PopoverPrimitive.Portal>
      <PopoverPrimitive.Content
        align={align}
        sideOffset={sideOffset}
        onOpenAutoFocus={(event) => {
          if (!autoFocus) event.preventDefault();
        }}
        className={cn(
          "z-50 border border-border bg-surface-raised p-1 shadow-lg",
          // Radix measures the trigger and publishes it; without this a
          // dropdown under a wide control renders at content width and the two
          // edges disagree.
          "min-w-[var(--radix-popover-trigger-width)]",
          "max-h-[min(24rem,var(--radix-popover-content-available-height))] overflow-y-auto",
          className,
        )}
      >
        {children}
      </PopoverPrimitive.Content>
    </PopoverPrimitive.Portal>
  );
}
